import { localizedRoutes } from "@/lib/localizedRoutes/localizedRoutes";
import {
  makeLocalizedHrefNoLocale,
  makePublicHref,
  norm,
  stripVisibleLocalePrefix,
  translatePath,
} from "@/lib/localizedRoutes/routeHelpers";
import { resolveMarket } from "@/lib/markets/resolve-market";

function getRouteKeyByFirstSegment(locale: string, firstSegment?: string) {
  if (!firstSegment) return "home";

  const first = norm(firstSegment);

  for (const [routeKey, locales] of Object.entries(localizedRoutes)) {
    if (norm(locales[locale]) === first) {
      return routeKey;
    }
  }

  return null;
}

function appendSearchParams(href: string, searchParams?: string) {
  if (!searchParams) return href;

  return `${href}${href.includes("?") ? "&" : "?"}${searchParams}`;
}

function getLocalHomeHref({
  toLocale,
  toDomain,
}: {
  toLocale: string;
  toDomain: string;
}) {
  return makePublicHref({
    domain: toDomain,
    locale: toLocale,
    pathname: "/",
  });
}

function getInternationalHomeHref({
  toLocale,
  toDomain,
}: {
  toLocale: string;
  toDomain: string;
}) {
  const targetMarket = resolveMarket({ domain: toDomain, locale: toLocale });

  const pathname = targetMarket.isGlobal
    ? "/"
    : makeLocalizedHrefNoLocale(toLocale, "international-real-estate");

  return makePublicHref({
    domain: toDomain,
    locale: toLocale,
    pathname,
  });
}

export function translateExperiencePath({
  pathname,
  fromLocale,
  toLocale,
  fromDomain,
  toDomain,
  searchParams,
}: {
  pathname: string;
  fromLocale: string;
  toLocale: string;
  fromDomain: string;
  toDomain: string;
  searchParams?: string;
}) {
  const pathnameWithoutLocalePrefix = stripVisibleLocalePrefix({
    domain: fromDomain,
    pathname,
  });

  const segments = pathnameWithoutLocalePrefix.split("/").filter(Boolean);
  const routeKey = getRouteKeyByFirstSegment(fromLocale, segments[0]);

  if (!routeKey || routeKey === "home") {
    return getLocalHomeHref({ toLocale, toDomain });
  }

  if (routeKey === "search") {
    return getLocalHomeHref({ toLocale, toDomain });
  }

  if (routeKey !== "international-real-estate") {
    const translatedPath = translatePath(
      pathnameWithoutLocalePrefix,
      fromLocale,
      toLocale,
    );

    const publicHref = makePublicHref({
      domain: toDomain,
      locale: toLocale,
      pathname: translatedPath,
    });

    return appendSearchParams(publicHref, searchParams);
  }

  const [, next] = segments;
  const internationalHomeHref = getInternationalHomeHref({
    toLocale,
    toDomain,
  });

  if (!next) {
    return appendSearchParams(internationalHomeHref, searchParams);
  }

  // International children use backend/database slugs.
  // Collapse to international home until backend route resolver exists.
  return internationalHomeHref;
}
